import React from "react";
import styled from "styled-components";
import { Button, ButtonType } from "../Button";
import Portal from "../common/Portal";
import { SvgCloseLarge } from "../Svgs/CloseLarge";
import { Body1, H2 } from "../Text";
import { colors } from "../Theme/colors";
import { useOverlay } from "../utils/hooks";

const Div = styled.div``;

export const ModalContainer = styled(Div)`
  top: 0px;
  left: 0px;
  margin: 0px;
  position: fixed;
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 100;
  width: 100vw;
  height: 100vh;
  margin-top: 8vh;
  pointer-events: none;
  div {
    pointer-events: auto;
  }
`;

export const ModalBackdrop = styled(Div)`
  position: fixed;
  top: 0px;
  left: 0px;
  margin: 0px;
  width: 100vw;
  height: 100vh;
  z-index: 100;
  background: black;
  opacity: 0.8;
`;

export const ModalWrapper = styled(Div)`
  background: ${(props) => props.theme.colors.white};
  padding: 38px 48px;
  border-radius: 8px;
  box-shadow: 0 0 24px 0 rgba(0, 0, 0, 0.1);
  overflow-y: auto;
  max-height: 80vh;
  margin-bottom: 100px;
  ${(props) => props.theme.mediaQueries.mobile} {
    width: 90vw;
    padding: 30px;
  }
`;

export const ModalCloseIcon = styled(Div)`
  height: 0;
  display: flex;
  flex-direction: row-reverse;
  background-color: #fff;
`;

const ModalTypes = ["small", "medium", "large"] as const;
export type ModalType = (typeof ModalTypes)[number];

type ModalWidths = {
  [K in ModalType]?: any;
};
const modalWidths: ModalWidths = {
  small: 476,
  medium: 676,
  large: 876,
};

type ModalConfirmation = {
  isDisabled?: boolean;
  label?: string;
  variant?: ButtonType;
  onConfirm: () => void;
};

type ModalProps = {
  isOpen?: boolean;
  onClose?: () => void;
  children?: any;
  header?: string;
  variant?: ModalType;
  body?: string;
  withCloseIcon?: boolean;
  withBackdropClose?: boolean;
  className?: string;
  confirmation?: ModalConfirmation;
  props?: any;
};
export const Modal = ({
  isOpen = false,
  onClose = () => {},
  header,
  body,
  variant = "small",
  children,
  withCloseIcon = true,
  withBackdropClose = true,
  className = "",
  confirmation,
  props,
}: ModalProps) => {
  useOverlay(isOpen);

  const headerUI = (
    <Div style={{ display: "flex", justifyContent: "space-between" }}>
      <H2 style={{ marginRight: 12 }}>{header}</H2>
      {withCloseIcon && (
        <ModalCloseIcon>
          <Div onClick={onClose} style={{ color: colors.black60 }}>
            <SvgCloseLarge />
          </Div>
        </ModalCloseIcon>
      )}
    </Div>
  );

  const confirmationUI = confirmation && (
    <Div style={{ display: "flex", justifyContent: "flex-end", marginTop: 32 }}>
      <Div style={{ display: "flex" }}>
        <Button variant="tertiary" onClick={onClose} style={{ width: 82 }}>
          Cancel
        </Button>
        <Button
          isDisabled={!!confirmation?.isDisabled || false}
          variant={confirmation.variant}
          onClick={() => {
            confirmation?.onConfirm();
            onClose();
          }}
          style={{ marginLeft: 20, display: "inline-flex" }}
        >
          {confirmation.label || "Confirm"}
        </Button>
      </Div>
    </Div>
  );

  return (
    <>
      {isOpen && (
        <Portal>
          <Div className={className}>
            <ModalBackdrop
              onClick={() => {
                if (withBackdropClose) onClose();
              }}
            />
            <ModalContainer>
              <ModalWrapper style={{ width: modalWidths[variant] }} {...props}>
                {headerUI}
                <Div>
                  {body && <Body1 style={{ marginTop: 20 }}>{body}</Body1>}
                  {children}
                </Div>
                {confirmationUI}
              </ModalWrapper>
            </ModalContainer>
          </Div>
        </Portal>
      )}
    </>
  );
};
